import { useState } from 'react';
import Card from 'components/card';
import { MdHistory, MdPerson, MdLogin, MdEdit, MdEmail, MdCheck, MdFilterList, MdCalendarToday, MdVideoCall, MdEventAvailable } from 'react-icons/md';

interface UserActivity {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  action: 'signup' | 'login' | 'profile_update' | 'meeting_scheduled' | 'meeting_completed' | 'meeting_cancelled' | 'invitation_sent' | 'invitation_accepted';
  details?: string;
  ipAddress?: string;
  device?: string;
  timestamp: string;
}

const UserActivityPage = () => {
  const [activities] = useState<UserActivity[]>([
    {
      id: '1',
      userId: 'u1',
      userName: 'John Doe',
      userEmail: 'john.doe@example.com',
      action: 'signup',
      details: 'New user registration',
      ipAddress: '192.168.1.1',
      device: 'Chrome on Windows',
      timestamp: '2025-01-09T10:30:00.000Z'
    },
    {
      id: '2',
      userId: 'u2',
      userName: 'Jane Smith',
      userEmail: 'jane.smith@example.com',
      action: 'login',
      details: 'Successful login',
      ipAddress: '192.168.1.2',
      device: 'Safari on MacOS',
      timestamp: '2025-01-09T09:45:00.000Z'
    },
    {
      id: '3',
      userId: 'u3',
      userName: 'Mike Johnson',
      userEmail: 'mike.j@example.com',
      action: 'meeting_scheduled',
      details: 'Scheduled mentorship session with Jane Smith for Jan 15, 2:00 PM',
      timestamp: '2025-01-09T08:20:00.000Z'
    },
    {
      id: '4',
      userId: 'u4',
      userName: 'Sarah Williams',
      userEmail: 'sarah.w@example.com',
      action: 'invitation_accepted',
      details: 'Accepted invitation and created account',
      ipAddress: '192.168.1.3',
      device: 'Firefox on Windows',
      timestamp: '2025-01-09T07:15:00.000Z'
    },
    {
      id: '5',
      userId: 'u2',
      userName: 'Jane Smith',
      userEmail: 'jane.smith@example.com',
      action: 'meeting_completed',
      details: 'Completed 1-hour mentorship session with Robert Brown',
      timestamp: '2025-01-08T16:30:00.000Z'
    },
    {
      id: '6',
      userId: 'u5',
      userName: 'Robert Brown',
      userEmail: 'robert.b@example.com',
      action: 'signup',
      details: 'New user registration',
      ipAddress: '192.168.1.4',
      device: 'Chrome on Android',
      timestamp: '2025-01-08T14:20:00.000Z'
    },
    {
      id: '7',
      userId: 'u7',
      userName: 'David Wilson',
      userEmail: 'david.w@example.com',
      action: 'meeting_cancelled',
      details: 'Cancelled upcoming session - schedule conflict',
      timestamp: '2025-01-07T15:45:00.000Z'
    },
    {
      id: '8',
      userId: 'u8',
      userName: 'Lisa Anderson',
      userEmail: 'lisa.a@example.com',
      action: 'signup',
      details: 'New user registration via invitation',
      ipAddress: '192.168.1.5',
      device: 'Safari on iOS',
      timestamp: '2025-01-07T11:30:00.000Z'
    },
    {
      id: '9',
      userId: 'u9',
      userName: 'Tom Harris',
      userEmail: 'tom.h@example.com',
      action: 'meeting_scheduled',
      details: 'Scheduled career coaching session for Jan 20, 10:00 AM',
      timestamp: '2025-01-07T10:00:00.000Z'
    },
    {
      id: '10',
      userId: 'u10',
      userName: 'Emma Martinez',
      userEmail: 'emma.m@example.com',
      action: 'meeting_completed',
      details: 'Completed 30-min check-in with mentor John Doe',
      timestamp: '2025-01-06T14:30:00.000Z'
    }
  ]);

  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter activities based on type and search
  const filteredActivities = activities.filter(activity => {
    const matchesFilter = filterType === 'all' || activity.action === filterType;
    const matchesSearch = searchQuery === '' ||
      activity.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.userEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.details?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Get action icon
  const getActionIcon = (action: UserActivity['action']) => {
    switch (action) {
      case 'signup':
        return <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg"><MdPerson className="h-5 w-5 text-green-600 dark:text-green-400" /></div>;
      case 'login':
        return <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg"><MdLogin className="h-5 w-5 text-blue-600 dark:text-blue-400" /></div>;
      case 'profile_update':
        return <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg"><MdEdit className="h-5 w-5 text-purple-600 dark:text-purple-400" /></div>;
      case 'meeting_scheduled':
        return <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg"><MdCalendarToday className="h-5 w-5 text-blue-600 dark:text-blue-400" /></div>;
      case 'meeting_completed':
        return <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg"><MdEventAvailable className="h-5 w-5 text-green-600 dark:text-green-400" /></div>;
      case 'meeting_cancelled':
        return <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg"><MdVideoCall className="h-5 w-5 text-red-600 dark:text-red-400" /></div>;
      case 'invitation_sent':
        return <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg"><MdEmail className="h-5 w-5 text-indigo-600 dark:text-indigo-400" /></div>;
      case 'invitation_accepted':
        return <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg"><MdCheck className="h-5 w-5 text-green-600 dark:text-green-400" /></div>;
      default:
        return <div className="p-2 bg-gray-100 dark:bg-gray-900/20 rounded-lg"><MdHistory className="h-5 w-5 text-gray-600 dark:text-gray-400" /></div>;
    }
  };

  // Get action label
  const getActionLabel = (action: UserActivity['action']) => {
    switch (action) {
      case 'signup': return 'Signed Up';
      case 'login': return 'Logged In';
      case 'profile_update': return 'Updated Profile';
      case 'meeting_scheduled': return 'Scheduled Meeting';
      case 'meeting_completed': return 'Completed Meeting';
      case 'meeting_cancelled': return 'Cancelled Meeting';
      case 'invitation_sent': return 'Invitation Sent';
      case 'invitation_accepted': return 'Invitation Accepted';
      default: return 'Activity';
    }
  };

  // Format relative time
  const formatRelativeTime = (timestamp: string) => {
    const now = new Date();
    const activityDate = new Date(timestamp);
    const diffMs = now.getTime() - activityDate.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return activityDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: activityDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };


  return (
    <div className="mt-3 h-full w-full">
      {/* Coming Soon Banner */}
      <div className="mb-4 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 font-medium">
            Coming Soon
          </span>
          <p className="text-sm text-yellow-700 dark:text-yellow-400">
            This feature is currently under development and will be available soon.
          </p>
        </div>
      </div>
      {/* Activity Feed */}
      <Card extra="w-full pb-10 p-6 h-full">
        <div className="mb-6">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Recent User Activity
          </h4>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Monitor user actions and system events in real-time
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          {/* Search */}
          <div className="flex-1 md:max-w-xs">
            <input
              type="text"
              placeholder="Search by name, email, or activity..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center gap-2">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <div className="flex gap-2">
              {['all', 'signup', 'login', 'meeting_scheduled'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFilterType(filter)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${filterType === filter
                      ? 'bg-brand-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                >
                  {filter === 'all' ? 'All' : filter.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Activity List */}
        <div className="space-y-4">
          {filteredActivities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start gap-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4 transition-all hover:bg-gray-50 dark:hover:bg-navy-800"
            >
              {/* Icon */}
              {getActionIcon(activity.action)}

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-navy-700 dark:text-white">
                        {activity.userName}
                      </span>
                      <span className="text-sm text-gray-500">
                        {getActionLabel(activity.action)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {activity.userEmail}
                    </p>
                    {activity.details && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {activity.details}
                      </p>
                    )}
                    {(activity.ipAddress || activity.device) && (
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                        {activity.ipAddress && <span>IP: {activity.ipAddress}</span>}
                        {activity.device && <span>{activity.device}</span>}
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(activity.timestamp)}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {filteredActivities.length === 0 && (
            <div className="py-12 text-center">
              <MdHistory className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                No activities found matching your criteria
              </p>
            </div>
          )}
        </div>

        {/* Load More */}
        {filteredActivities.length > 0 && (
          <div className="mt-6 flex justify-center">
            <button
              disabled
              title="Coming Soon"
              className="rounded-lg border border-gray-300 dark:border-gray-600 px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-navy-800 transition-all disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              Load More Activities
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserActivityPage;